import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxCompatibility = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (Array.isArray(inboxCompatibility) && inboxCompatibility.length) {
        filteredRecords = filteredRecords.filter(record => {
          if (!record.inbox) {
            return false;
          }

          const channelType = record.inbox.channel_type;
          const medium = record.inbox.medium;

          return inboxCompatibility.some(rule => {
            if (!rule) {
              return false;
            }

            if (typeof rule === 'string') {
              return channelType === rule;
            }

            if (typeof rule === 'object') {
              if (rule.channelType && channelType !== rule.channelType) {
                return false;
              }

              if (rule.medium && medium !== rule.medium) {
                return false;
              }

              return true;
            }

            return false;
          });
        });
      }

      return filteredRecords.sort((a1, a2) => a2.id - a1.id);
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsCompatibility = [
      { channelType: INBOX_TYPES.SMS },
      { channelType: INBOX_TYPES.TWILIO, medium: 'sms' },
    ];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsCompatibility);
  },
  getWhatsAppCampaigns: _state => {
    const WHATSAPP_PROVIDERS = [
      'evolution',
      'waha',
      'uazapi',
      'baileys',
      'notificame',
      'whatsapp_cloud',
    ];
    const isWhatsAppInbox = inbox => {
      if (!inbox) return false;
      const ct = inbox.channel_type;
      if (ct === INBOX_TYPES.WHATSAPP) return true;
      if (ct === INBOX_TYPES.TWILIO && inbox.medium === 'whatsapp') return true;
      if (ct === INBOX_TYPES.API) {
        const attrs = inbox.additional_attributes || {};
        if (attrs.channel_identifier === 'whatsapp') return true;
        const provider = (inbox.provider || attrs.provider || '').toLowerCase();
        if (WHATSAPP_PROVIDERS.includes(provider)) return true;
        if (inbox.name?.toLowerCase().includes('whatsapp')) return true;
      }
      return false;
    };
    return _state.records
      .filter(
        r =>
          r.campaign_type === CAMPAIGN_TYPES.ONE_OFF && isWhatsAppInbox(r.inbox)
      )
      .sort((a, b) => b.id - a.id);
  },
  getTelegramCampaigns: (_state, _getters) => {
    const telegramCompatibility = [{ channelType: INBOX_TYPES.TELEGRAM }];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, telegramCompatibility);
  },
  getApiCampaigns: (_state, _getters) => {
    const apiCompatibility = [{ channelType: INBOX_TYPES.API }];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, apiCompatibility);
  },
  getEmailCampaigns: (_state, _getters) => {
    const emailCompatibility = [{ channelType: INBOX_TYPES.EMAIL }];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, emailCompatibility);
  },
  getLiveChatCampaigns: (_state, _getters) => {
    const liveChatCompatibility = [{ channelType: INBOX_TYPES.WEB }];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONGOING, liveChatCompatibility);
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
};

export const actions = {
  get: async function getCampaigns({ commit }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get();
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
      return response.data;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  estimateCost: async (_, { campaignId, audienceCount }) => {
    const response = await CampaignsAPI.estimateCost(campaignId, {
      audience_count: audienceCount,
    });
    return response.data;
  },
  aiPreview: async (_, { campaignId, sampleContactIds }) => {
    const response = await CampaignsAPI.aiPreview(campaignId, {
      sample_contact_ids: sampleContactIds,
    });
    return response.data;
  },
  pauseCampaign: async ({ commit }, id) => {
    const response = await CampaignsAPI.pause(id);
    commit(types.EDIT_CAMPAIGN, { id, campaign_status: 'paused' });
    return response.data;
  },
  resumeCampaign: async ({ commit }, id) => {
    const response = await CampaignsAPI.resume(id);
    commit(types.EDIT_CAMPAIGN, { id, campaign_status: 'running' });
    return response.data;
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
